/**
 * Effects without a framework: each reaction names what it depends on, and
 * runs again (after cleaning up its previous run) only when one of those
 * values has changed, compared with `Object.is`. It is React's effect rule,
 * kept so the checkout's side effects fire exactly when they did while they
 * lived in the component.
 */
export type Reaction<TView> = {
  /** What this reaction reads from the view to decide whether to run. */
  deps: (view: TView) => ReadonlyArray<unknown>;
  run: (view: TView) => void | (() => void);
};

export function createReactions<TView>(list: ReadonlyArray<Reaction<TView>>) {
  const last: Array<ReadonlyArray<unknown> | null> = list.map(() => null);
  const cleanups: Array<(() => void) | undefined> = list.map(() => undefined);
  const changed = (a: ReadonlyArray<unknown> | null, b: ReadonlyArray<unknown>) =>
    a === null ||
    a.length !== b.length ||
    a.some((value, index) => !Object.is(value, b[index]));
  return {
    /** Run every reaction whose dependencies changed, in declaration order,
     *  all against the same view. */
    run(view: TView) {
      list.forEach((reaction, index) => {
        const deps = reaction.deps(view);
        if (!changed(last[index], deps)) return;
        last[index] = deps;
        cleanups[index]?.();
        cleanups[index] = reaction.run(view) ?? undefined;
      });
    },
    /** Clean every reaction up and forget what they last saw, so that a
     *  later `run` starts them all afresh — a stop, not an end. */
    reset() {
      for (const cleanup of cleanups) cleanup?.();
      cleanups.fill(undefined);
      last.fill(null);
    },
  };
}
